import React, { useEffect, useRef, useState } from 'react';
import './Location.css';
// react-map-gl is pinned to v7 — v8+ uses an ES Module exports map that react-scripts's locked Webpack config cannot resolve.
import Map, { NavigationControl, GeolocateControl, Marker } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useAuth } from '../../context/AuthContext';
import { useFireController } from '../../context/FireControllerContext';
import { useSystem } from '../../context/SystemContext';
import { TOAST_VARIANT, useToast } from '../../context/ToastContext';
import SystemStatus from '../../generated/src/model/SystemStatus';
import useMarkerData from '../../hooks/useMarkerData';
import MarkerPopup from '../../components/MarkerPopup';
import { isCollaborativeDefenseEngaged } from '../../utils/systemStatusUtils';

// Default map zoom level
const DEFAULT_ZOOM = 15;
// Coordinates for the Camarillo Outlets
const DEFAULT_CENTER = { latitude: 34.21631605560901, longitude: -119.06185939166876, zoom: DEFAULT_ZOOM };
// Mapbox map style to set the view to
const MAP_STYLE = 'mapbox://styles/mapbox/streets-v12';
// SVG path for the standard Mapbox teardrop pin shape (27×41 viewBox)
const MARKER_PIN_PATH = 'M13.5 0C6.044 0 0 6.044 0 13.5c0 8.857 12.165 24.036 12.686 24.678L13.5 39l.814-1.322C14.835 37.036 27 22.357 27 13.5 27 6.044 20.956 0 13.5 0z';
// Viewport width below which the layout is treated as mobile.
const MOBILE_BREAKPOINT_PX = 768;
// Fraction of viewport height used as bottom padding so flyTo centers within the visible area above the popup.
const POPUP_BOTTOM_PADDING_RATIO = 0.50;
const MANUAL_RESET_ACCEPTED_MESSAGE = 'Manual Reset command accepted.';
const MANUAL_RESET_FAILED_MESSAGE = 'Manual Reset request failed. Please try again.';

export const MARKER_COLORS = {
  GREEN:  '#4caf50',
  ORANGE: '#ff9800',
  RED:    '#e53935',
  GRAY:   '#9e9e9e',
  PURPLE: '#8e24aa',
};

const { SharedModeEnum } = SystemStatus;

const SHARED_MODE_COLOR_MAP = {
  [SharedModeEnum.OFF]:          MARKER_COLORS.GREEN,
  [SharedModeEnum.PRIMING]:      MARKER_COLORS.ORANGE,
  [SharedModeEnum.ACTIVATING]:   MARKER_COLORS.ORANGE,
  [SharedModeEnum.DEACTIVATING]: MARKER_COLORS.ORANGE,
  [SharedModeEnum.ON]:           MARKER_COLORS.RED,
  [SharedModeEnum.ERROR]:        MARKER_COLORS.GRAY,
  [SharedModeEnum.UNINITIALIZED]: MARKER_COLORS.GRAY,
};

const LEGEND_ITEMS = [
  { color: MARKER_COLORS.GREEN,  label: 'Standby' },
  { color: MARKER_COLORS.ORANGE, label: 'Priming' },
  { color: MARKER_COLORS.RED,    label: 'Active' },
  { color: MARKER_COLORS.PURPLE, label: 'Collaborative Active' },
  { color: MARKER_COLORS.GRAY,   label: 'Error / Disconnected' },
];

/**
 * Returns the marker color for a given system status.
 * @param {Object|null} systemStatus - SystemStatus object, or null when device is offline
 * @returns {string} hex color string
 */
function markerColor(systemStatus) {
  if (systemStatus == null) return MARKER_COLORS.GRAY;
  if (isCollaborativeDefenseEngaged(systemStatus)) return MARKER_COLORS.PURPLE;
  return SHARED_MODE_COLOR_MAP[systemStatus.sharedMode] ?? MARKER_COLORS.GRAY;
}

const MapLegend = () => (
  <div className="map-legend">
    <div className="map-legend-title">Fire Controller Status</div>
    {LEGEND_ITEMS.map(({ color, label }) => (
      <div key={label} className="map-legend-item">
        <span className="map-legend-dot" style={{ backgroundColor: color }} />
        <span className="map-legend-label">{label}</span>
      </div>
    ))}
  </div>
);

/**
 * Renders the Fire Controller location map, status legend, and selected marker details.
 * @param {Object} props - Component properties.
 * @param {string} [props.ipAddress] - Optional API address supplied to the Location page.
 * @returns {JSX.Element} The Location page.
 */
const Location = ({ ipAddress }) => {
  const { hasAdminRole, user } = useAuth();
  const { getSelectedFireController, selectedFireControllerId } = useFireController();
  const { manualReset } = useSystem();
  const { showToast } = useToast();
  const isAdmin = hasAdminRole();
  const markerData = useMarkerData(isAdmin, user?.sub);
  const [popupMarkerId, setPopupMarkerId] = useState(null);

  /**
   * Submits a Manual Reset command for the explicitly selected map marker.
   * @param {string} fireControllerId - Fire Controller ID supplied by MarkerPopup.
   * @returns {Promise<void>} Resolves after Fire Bridge accepts the command.
   * @throws {Error} Re-throws request failures so MarkerPopup can remain open for retry.
   */
  async function handleManualReset(fireControllerId) {
    try {
      await manualReset(fireControllerId);
      showToast(MANUAL_RESET_ACCEPTED_MESSAGE, TOAST_VARIANT.SUCCESS);
    } catch (error) {
      showToast(MANUAL_RESET_FAILED_MESSAGE);
      throw error;
    }
  }

  const popupEntry = popupMarkerId
    ? markerData.find(m => m.id === popupMarkerId) ?? null
    : null;

  const mapRef = useRef(null);

  const selected = getSelectedFireController();
  const hasCoords = selected?.latitude != null && selected?.longitude != null;
  const initialViewState = hasCoords
    ? { ...DEFAULT_CENTER, latitude: selected.latitude, longitude: selected.longitude }
    : DEFAULT_CENTER;

  // When the user selects a different Fire Controller, fly the map to that controller's location
  useEffect(() => {
    if (!mapRef.current) return;
    const fc = getSelectedFireController();
    const fcHasCoords = fc?.latitude != null && fc?.longitude != null;
    const center = fcHasCoords ? [fc.longitude, fc.latitude] : [DEFAULT_CENTER.longitude, DEFAULT_CENTER.latitude];
    mapRef.current.flyTo({ center, zoom: DEFAULT_ZOOM });
  }, [selectedFireControllerId]);

  return (
    <div className="location-page">
      <Map
        ref={mapRef}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
        initialViewState={initialViewState}
        style={{ width: '100%', height: '100%' }}
        mapStyle={MAP_STYLE}
      >
        <NavigationControl position="top-right" />
        <GeolocateControl position="top-right" />
        {markerData.map(fc => {
          const color = markerColor(fc.systemStatus);
          return (
            <Marker
              key={fc.id}
              latitude={fc.latitude}
              longitude={fc.longitude}
              anchor="bottom"
              onClick={() => {
                setPopupMarkerId(fc.id);
                const isMobile = window.innerWidth < MOBILE_BREAKPOINT_PX;
                mapRef.current?.flyTo({
                  center: [fc.longitude, fc.latitude],
                  zoom: DEFAULT_ZOOM,
                  padding: isMobile ? { bottom: Math.round(window.innerHeight * POPUP_BOTTOM_PADDING_RATIO) } : {},
                });
              }}
            >
              <div className="marker-container">
                {fc.streetAddr && (
                  <div className="marker-label">{fc.streetAddr}</div>
                )}
              </div>
              <svg
                display="block"
                height="30px"
                width="20px"
                viewBox="0 0 27 41"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  fill={color}
                  d={MARKER_PIN_PATH}
                />
                <circle fill="white" cx="13.5" cy="13.5" r="5.5" />
              </svg>
            </Marker>
          );
        })}
      </Map>
      <MapLegend />
      <MarkerPopup
        markerEntry={popupEntry}
        onClose={() => setPopupMarkerId(null)}
        isAdmin={isAdmin}
        onManualReset={handleManualReset}
      />
    </div>
  );
};

export default Location;
